// features/workstations/shell.js
// ═══════════════════════════════════════════════════════════
// WORKSTATION SHELL — 4-tab interior
//
// Layout: single column (topbar + scroll), matching Domain
// Intelligence visual rhythm. All navigation lives in the
// topbar: back arrow, sibling-strategy dropdown, horizontal
// tab strip with group separators.
//
// Loads the selected workstation by slug from the API and
// renders topbar + active tab body.
//
// Commit 97 (2026-04-27) — trimmed to four tabs (Dashboard,
// QQQ, Think, Knowledge). The five universal personal tabs
// (Findings / Signal Read / Experience / Watchlist / Resources)
// were removed because the QQQ tab now consolidates everything
// they would surface for the day-trading workstation. The tab
// component files remain on disk in case a future non-trading
// domain needs them; they are simply no longer wired in.
// ═══════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import WorkstationTopbar from './components/workstation-topbar';
import DashboardTab from './tabs/dashboard';
import QqqTab from './tabs/qqq';   // Commit 19 — restored after parallel-session merge
import ThinkTab from './tabs/think';
import KnowledgeTab from './tabs/knowledge';
import CatalystTab from './tabs/catalyst';   // Long-Term 10x only (gated in config-schema.js)
import PropertyDossierTab from './tabs/property-dossier';   // Wealth Building only (gated in config-schema.js)
import CloseTab from './tabs/close';   // Universal sales skill (gated in config-schema.js)
import ToolsTab from './tabs/tools';   // REI calculator suite (gated in config-schema.js)

const TAB_COMPONENTS = {
  dashboard:   DashboardTab,
  qqq:         QqqTab,
  think:       ThinkTab,
  knowledge:   KnowledgeTab,
  catalyst:    CatalystTab,
  deals:       PropertyDossierTab,
  tools:       ToolsTab,
  close:       CloseTab,
};

export default function WorkstationShell({
  userId,
  userRecord,
  supabase,
  slug,
  siblingWorkstations = [],
  onBackToHome,
  onSwitchWorkstation,
}) {
  const [workstation, setWorkstation] = useState(null);
  // Commit 70 — persist active tab per-workstation slug so refreshes
  // restore the user to the same tab they were on.
  const [activeTab, setActiveTabState] = useState(() => {
    if (typeof window === 'undefined') return 'dashboard';
    const stored = window.localStorage.getItem(`shift_ws_tab:${slug}`);
    return stored && TAB_COMPONENTS[stored] ? stored : 'dashboard';
  });
  const setActiveTab = (next) => {
    setActiveTabState(next);
    if (typeof window !== 'undefined') {
      try { window.localStorage.setItem(`shift_ws_tab:${slug}`, next); }
      catch { /* ignore */ }
    }
  };
  // When the user switches workstations (slug changes), re-pull the
  // tab they last had open in THAT workstation.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = window.localStorage.getItem(`shift_ws_tab:${slug}`);
    if (stored && TAB_COMPONENTS[stored]) {
      setActiveTabState(stored);
    } else {
      setActiveTabState('dashboard');
    }
  }, [slug]);
  const [loading, setLoading] = useState(true);

  // Load this workstation by slug
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const qs = userId ? `?user_id=${encodeURIComponent(userId)}` : '';
        const res = await fetch(`/api/workstations/${encodeURIComponent(slug)}${qs}`, { cache: 'no-store' });
        if (!res.ok) {
          if (!cancelled) setLoading(false);
          return;
        }
        const data = await res.json();
        if (cancelled) return;
        setWorkstation(data.workstation || null);
      } catch (err) {
        console.warn('[workstation-shell] load failed:', err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, userId]);

  if (loading) {
    return (
      <div className="ws-shell">
        <div className="ws-loading">Loading workstation…</div>
      </div>
    );
  }

  if (!workstation) {
    return (
      <div className="ws-shell">
        <div className="ws-loading">Workstation not found · slug: {slug}</div>
      </div>
    );
  }

  const TabComponent = TAB_COMPONENTS[activeTab] || DashboardTab;

  return (
    <div className="ws-shell">
      <WorkstationTopbar
        workstation={workstation}
        siblingWorkstations={siblingWorkstations}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onSwitchWorkstation={onSwitchWorkstation}
        onBackToHome={onBackToHome}
      />
      {/* Each tab owns its own .ws-tab-content scroll container */}
      <TabComponent
        workstation={workstation}
        userId={userId}
        userRecord={userRecord}
        supabase={supabase}
      />
    </div>
  );
}
